<template>
  <div class="ledger-page">
    <div
      v-if="loading"
      class="text-center py-3"
    >
      <b-spinner variant="primary" />
    </div>
    <b-card
      v-else-if="run"
      class="rounded-lg"
    >
      <div class="d-flex justify-content-between align-items-start flex-wrap">
        <div>
          <h4 class="mb-50">
            {{ periodLabel }}
          </h4>
          <b-badge :variant="statusBadgeVariant(enumValue(run.status))">
            {{ enumLabel(run.status, $i18n.locale) }}
          </b-badge>
        </div>
        <b-button
          v-if="canReverse"
          variant="outline-danger"
          size="sm"
          class="rounded-pill"
          @click="confirmReverse"
        >
          {{ $t('ledger_depr_reverse') }}
        </b-button>
      </div>
      <div class="ledger-num mt-1">
        {{ money(run.total_amount) }} · {{ run.asset_count }}
      </div>
      <div class="mt-1">
        <b-link
          v-if="journalId && canOpenJournal"
          class="d-inline-block mr-1"
          :to="journalRoute(journalId)"
        >
          {{ $t('ledger_journal_open') }}
        </b-link>
        <b-link
          v-if="reversalId && canOpenJournal"
          class="d-inline-block"
          :to="journalRoute(reversalId)"
        >
          {{ $t('ledger_depr_reversal_journal') }}
        </b-link>
      </div>
      <b-table
        v-if="entries.length"
        class="ledger-dense-table mt-1"
        :items="entries"
        :fields="fields"
        small
        responsive
      >
        <template #cell(asset)="data">
          {{ assetLabel(data.item.asset || data.item) }}
        </template>
        <template #cell(amount)="data">
          <span class="ledger-num">{{ money(data.item.amount) }}</span>
        </template>
        <template #cell(book_value_after)="data">
          <span class="ledger-num">{{ money(data.item.book_value_after) }}</span>
        </template>
      </b-table>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BBadge, BButton, BLink, BTable, BSpinner,
} from 'bootstrap-vue'
import Swal from 'sweetalert2'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { getDepreciationRun, reverseDepreciationRun } from '@/api/accounting/fixedAssets'
import {
  oneFrom, enumValue, enumLabel, formatLedgerMoney, statusBadgeVariant, apiErrorMessage,
} from '@/utils/ledger'

export default {
  name: 'DepreciationRunShow',
  components: {
    BCard, BBadge, BButton, BLink, BTable, BSpinner,
  },
  data() {
    return {
      loading: false,
      run: null,
    }
  },
  computed: {
    canReverse() {
      return this.hasPermission('run depreciation') && enumValue(this.run && this.run.status) === 'posted'
    },
    canOpenJournal() {
      return this.hasPermission('view journal_entries')
    },
    journalId() {
      if (!this.run) return null
      return this.run.journal_entry_id || (this.run.journal_entry && this.run.journal_entry.id) || null
    },
    reversalId() {
      if (!this.run) return null
      return this.run.reversal_journal_entry_id || (this.run.reversal_journal_entry && this.run.reversal_journal_entry.id) || null
    },
    entries() {
      return (this.run && this.run.entries) || []
    },
    periodLabel() {
      if (!this.run) return ''
      const loc = this.$i18n.locale === 'en' ? 'en-EG' : 'ar-EG'
      const month = new Intl.DateTimeFormat(loc, { month: 'long' }).format(new Date(2020, this.run.month - 1, 1))
      return `${month} ${this.run.year}`
    },
    fields() {
      return [
        { key: 'asset', label: this.$t('ledger_assets_register') },
        { key: 'amount', label: this.$t('ledger_assets_amount') },
        { key: 'book_value_after', label: this.$t('ledger_assets_nbv') },
      ]
    },
  },
  watch: {
    '$route.params.id': 'load',
  },
  mounted() {
    this.load()
  },
  methods: {
    enumValue,
    enumLabel,
    statusBadgeVariant,
    money(value) {
      return formatLedgerMoney(value || 0, this.$i18n.locale)
    },
    assetLabel(asset) {
      if (!asset) return ''
      const name = this.$i18n.locale === 'en' ? (asset.name || asset.name_ar) : (asset.name_ar || asset.name)
      return asset.asset_code ? `${asset.asset_code} — ${name || ''}` : (name || asset.fixed_asset_id || '')
    },
    journalRoute(id) {
      return { name: 'ledger-journals-view', params: { id } }
    },
    async load() {
      this.loading = true
      try {
        const res = await getDepreciationRun(this.$route.params.id)
        this.run = oneFrom(res)
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: apiErrorMessage(error, this.$t('ledger_error')),
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.loading = false
      }
    },
    async confirmReverse() {
      if (!this.canReverse) return
      const result = await Swal.fire({
        text: this.$t('ledger_depr_reverse_confirm'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('ledger_depr_reverse'),
        cancelButtonText: this.$t('ledger_cancel'),
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      })
      if (!result.isConfirmed) return
      try {
        await reverseDepreciationRun(this.run.id)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('ledger_depr_reversed'),
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        await this.load()
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: apiErrorMessage(error, this.$t('ledger_error')),
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      }
    },
  },
}
</script>

<style scoped>
.ledger-num {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
</style>
